/**
 * PLACES — the venue-led half of the events/venues split.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE QUESTION THIS PAGE ANSWERS
 *
 *   "Where's a pub with a pool table?"
 *
 * EVENTS_AUDIT.md §2.2 recorded that as flatly unanswerable — "by API, by UI
 * and by data". The venue amenity list was a flat `text[]` that nothing
 * queried, the modern `venue_facilities` join table had 0 rows, and the two
 * vocabularies agreed on exactly 1 of 11 tokens.
 *
 * All three are now fixed:
 *   • Phase 3 backfilled `venue_facilities` (16 rows) through a hand-authored
 *     alias map.
 *   • Phase 4 added `?facility=` to `GET /bars` and `GET /venues`, matching
 *     against BOTH the join table and the legacy array.
 *   • This page is the UI.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * FILTER SEMANTICS — read off the server, not assumed
 *
 * From `api-server/src/lib/facilityFilter.ts`:
 *   • `?facility=pool-table,beer-garden` — comma-separated or repeated
 *   • **AND**: a venue must have EVERY requested facility
 *   • canonical slugs and legacy underscore tokens both accepted
 *   • absent = no filter; capped at 10 distinct slugs
 *
 * AND (not OR) is the right default for this question and it is what the
 * server does, so the chips are additive and the copy says "with a pool table
 * and a beer garden". A filter whose `match` list holds several slugs
 * (Live Sport → sports-tv | sky-sports | tnt-sports) sends only its FIRST
 * slug, because sending all of them would AND them together and demand a pub
 * with four separate TV packages. That is a real limitation of AND-only
 * server semantics and is called out in the report.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT IS ON A VENUE AND WHAT IS NOT
 *
 * Facilities are on the venue. Events are NOT — the "What's on here" line is
 * resolved by reference from `/api/events?venueId=…`, never copied onto the
 * venue row. That is the whole discipline this rewrite is about, and it is why
 * a venue can be listed with no events and an event can exist with no venue.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { CalendarDays, ChevronRight, Plus, X } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";
import { useTown } from "@/hooks/use-town";
import { useAuth } from "@/hooks/use-auth";
import { useAnalytics } from "@/hooks/use-analytics";
import { QueryState } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ALL_TOWNS, BROWSE_GRID, ELEVATION, FOCUS_RING, FilterChip, PLACE_FILTERS,
  PLACE_FILTER_BY_SLUG, RADII, TAP_TARGET, TYPE, TownPicker, VenueCard,
  VenueCardSkeleton, resolveFacilities, shortDate, townFromSlug, townSlug,
  useEvents, useFacilityDefinitions, useVenueFacilityMap, useVenues,
  type HubEvent, type HubVenue, type PlaceFilter,
} from "@/components/hub";

/** Sits directly below the sticky 64px site navbar (Navbar.tsx: `h-16`). */
const STICKY_TOP = "top-16";

/* ── URL ⇄ state ──────────────────────────────────────────────────────────
     /places                          everything
     /places/:town                    town
     /places/:town/:facility          town + one facility (the shareable form)
     ?with=a,b                        additional facilities

   `/places/kilmarnock/pool` is the URL a person would send a friend, so the
   first selected filter is promoted into the path and the rest ride in the
   query string.                                                             */

function parsePath(pathname: string): { townSlugFromPath: string | null; filterSlugFromPath: string | null } {
  const segments = pathname.split("?")[0]!.split("/").filter(Boolean);
  const [, town, facility] = segments; // ["places", town, facility]
  return { townSlugFromPath: town ?? null, filterSlugFromPath: facility ?? null };
}

function buildPath(town: string, filters: readonly string[]): string {
  const hasTown = town !== ALL_TOWNS;
  const [first, ...rest] = filters;

  const path =
    hasTown && first ? `/places/${townSlug(town)}/${first}`
    : hasTown ? `/places/${townSlug(town)}`
    : "/places";

  const query = new URLSearchParams();
  // Without a town there is nowhere in the path for a facility, so all of them
  // ride in `?with=`. With a town, only the overflow does.
  const carried = hasTown ? rest : filters;
  if (carried.length > 0) query.set("with", carried.join(","));
  const q = query.toString();
  return q ? `${path}?${q}` : path;
}

/** Human phrase for the selected filters: "with a pool table and darts". */
function filterPhrase(filters: readonly PlaceFilter[]): string {
  const facilities = filters.filter((f) => f.kind === "facility").map((f) => f.label.toLowerCase());
  if (facilities.length === 0) return "";
  if (facilities.length === 1) return `with ${facilities[0]}`;
  return `with ${facilities.slice(0, -1).join(", ")} and ${facilities.at(-1)}`;
}

/** Plural noun for the selected venue-type filters: "pubs", "clubs", "places". */
function subjectNoun(filters: readonly PlaceFilter[]): string {
  const types = filters.filter((f) => f.kind === "type");
  if (types.length === 1) return types[0]!.label.toLowerCase();
  return "places";
}

export default function Places() {
  const [location, navigate] = useLocation();
  const { track } = useAnalytics();
  const { user } = useAuth();
  const { selectedTown, setSelectedTown, towns } = useTown();

  const route = useMemo(() => parsePath(location), [location]);
  const search = typeof window !== "undefined" ? window.location.search : "";

  const townFromUrl = route.townSlugFromPath ? townFromSlug(route.townSlugFromPath, towns) : null;
  const town = townFromUrl ?? selectedTown;
  const previousGlobalTown = useRef(selectedTown);
  useEffect(() => {
    if (previousGlobalTown.current === selectedTown) return;
    previousGlobalTown.current = selectedTown;
    if (townFromUrl && townFromUrl !== selectedTown) apply(selectedTown, selected);
  }, [selectedTown]);


  const [selected, setSelected] = useState<string[]>(() => {
    const fromQuery = new URLSearchParams(search).get("with")?.split(",").filter(Boolean) ?? [];
    return [...new Set([route.filterSlugFromPath, ...fromQuery].filter(Boolean) as string[])]
      .filter((s) => !!PLACE_FILTER_BY_SLUG[s]);
  });

  /* Re-sync on navigation (back button, a shared /places/kilmarnock/pool link). */
  useEffect(() => {
    const parsed = parsePath(location);
    const fromQuery = new URLSearchParams(window.location.search).get("with")?.split(",").filter(Boolean) ?? [];
    const next = [...new Set([parsed.filterSlugFromPath, ...fromQuery].filter(Boolean) as string[])]
      .filter((s) => !!PLACE_FILTER_BY_SLUG[s]);
    setSelected((prev) => (prev.join(",") === next.join(",") ? prev : next));
    const t = parsed.townSlugFromPath ? townFromSlug(parsed.townSlugFromPath, towns) : null;
    if (t && t !== selectedTown) setSelectedTown(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => { track("page_viewed", { screen: "places" }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilters = useMemo(
    () => selected.map((s) => PLACE_FILTER_BY_SLUG[s]).filter(Boolean) as PlaceFilter[],
    [selected],
  );

  const townParam = town !== ALL_TOWNS ? town : undefined;
  const townLabel = town === ALL_TOWNS ? "Ayrshire" : town;
  const phrase = filterPhrase(activeFilters);
  const noun = subjectNoun(activeFilters);

  useSEO({
    title: `${noun[0]!.toUpperCase()}${noun.slice(1)} in ${townLabel}${phrase ? ` ${phrase}` : ""}`,
    description:
      `Find pubs, bars and clubs in ${townLabel} by what they actually have — pool table, dart board, live sport, jukebox, beer garden, function room and more.`,
  });

  /* ── Query ──────────────────────────────────────────────────────────────
     Venue types are sent as `type`; facilities as `facility`. Only the first
     slug of each multi-slug facility filter is sent — see the header note on
     AND semantics.

     `type` accepts exactly one value on the server (`eq(venueType, …)`), so
     selecting both Pubs and Clubs cannot be expressed server-side. In that
     case we send none and narrow the (small) result set here instead, which
     is honest about the limitation rather than silently dropping one chip. */

  const typeFilters = activeFilters.filter((f) => f.kind === "type");
  const facilityFilters = activeFilters.filter((f) => f.kind === "facility");

  const typeParam = typeFilters.length === 1 ? typeFilters[0]!.match[0] : undefined;
  const facilityParam = facilityFilters.length > 0
    ? facilityFilters.map((f) => f.match[0]).join(",")
    : undefined;

  const venuesQuery = useVenues({ town: townParam, type: typeParam, facility: facilityParam });
  const serverVenues = useMemo(() => venuesQuery.data?.items ?? [], [venuesQuery.data]);

  /* Venue-type narrowing, client side.
     `GET /bars` applies `eq(venueType, …)` — exactly one value — so "Pubs AND
     Clubs" cannot be expressed server-side. When both are selected we send no
     `type` at all and narrow here, rather than silently honouring one chip and
     dropping the other. When only one is selected the server already did it and
     this pass is a no-op. */
  const typeNarrowed = useMemo(() => {
    if (typeFilters.length === 0) return serverVenues;
    const allowed = new Set(typeFilters.flatMap((f) => f.match));
    return serverVenues.filter((v) => allowed.has(v.venueType));
  }, [serverVenues, typeFilters]);

  /* Facility slugs per venue — for the icon row on each card. */
  const { facilityMap, isLoading: facilitiesLoading } = useVenueFacilityMap(typeNarrowed);

  /* Facility narrowing, client side — a BELT-AND-BRACES pass, not the primary
     filter.
     `?facility=` is a real server filter (Phase 4), and when it is honoured
     this loop removes nothing. But an unknown query param is silently ignored
     by Express, so against an API build that predates Phase 4 the server
     returns everything and the page would confidently claim three pubs have a
     pool table when one does. A filter that lies is worse than a filter that
     is slow, so the predicate is re-applied here over the same union of
     `venue_facilities` and legacy amenities the server matches on.
     Cheap: the set is already in memory and capped at 50 rows. */
  const venues = useMemo(() => {
    if (facilityFilters.length === 0) return typeNarrowed;
    return typeNarrowed.filter((v) => {
      const slugs = facilityMap.get(v.id);
      if (!slugs) return false;
      // AND across chips, OR within a chip's synonym list — matching the
      // server's semantics, and the only sane reading of "Pool + Darts".
      return facilityFilters.every((f) => f.match.some((slug) => slugs.has(slug)));
    });
  }, [typeNarrowed, facilityFilters, facilityMap]);

  /* Facility data must have landed before a facility-filtered count can be
     trusted, otherwise the page flashes "0 places" and then fills in. */
  const isLoading = venuesQuery.isLoading || (facilityFilters.length > 0 && facilitiesLoading);

  const { data: defsData } = useFacilityDefinitions();
  const definitions = useMemo(() => defsData?.items ?? [], [defsData]);

  /* "What's on here" teasers, resolved BY REFERENCE. Nothing about an event is
     stored on the venue — this is one request for all upcoming events in the
     town, indexed by venueId, so adding a venue costs no extra round trip. */
  const eventsQuery = useEvents({ town: townParam, limit: 200 });
  const nextEventByVenue = useMemo(() => {
    const map = new Map<number, HubEvent>();
    for (const e of eventsQuery.data?.items ?? []) {
      if (!e.venueId) continue;
      const current = map.get(e.venueId);
      if (!current || e.eventDate < current.eventDate) map.set(e.venueId, e);
    }
    return map;
  }, [eventsQuery.data]);

  function whatsOnLine(venue: HubVenue): string | null {
    const next = nextEventByVenue.get(venue.id);
    if (next) return `${next.name} · ${shortDate(next.eventDate)}`;
    const special = venue.specials?.[0];
    return special ? special.title : null;
  }

  /* ── Interaction ────────────────────────────────────────────────────── */

  function apply(nextTown: string, nextFilters: string[]) {
    if (nextTown !== town) setSelectedTown(nextTown);
    setSelected(nextFilters);
    navigate(buildPath(nextTown, nextFilters), { replace: true });
  }

  const toggle = (slug: string) =>
    apply(town, selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);

  const hasFilter = selected.length > 0 || town !== ALL_TOWNS;

  const skeleton = (
    <div className={BROWSE_GRID}>
      {Array.from({ length: 8 }).map((_, i) => <VenueCardSkeleton key={i} />)}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Controls ─────────────────────────────────────────────────────
          Two rows then results, same budget as What's On and Order Food. */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 pt-3 pb-2">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className={cn(TYPE.footnote, "text-slate-500 hidden sm:block")}>
                Pubs, bars and clubs — by what they've actually got
              </p>
              <h1 className={cn(TYPE.title1, "text-[#1e3a5f]")}>Places</h1>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Link
                href="/events"
                className={cn(
                  TYPE.caption, RADII.pill, FOCUS_RING,
                  "hidden lg:inline-flex items-center gap-1.5 h-8 px-3 border border-slate-200 text-slate-600",
                  "hover:border-slate-300 hover:text-slate-900 transition-colors",
                )}
              >
                <CalendarDays size={13} aria-hidden /> What's On
              </Link>
              {user && (
                <Link href="/list-your-venue">
                  <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90">
                    <Plus size={14} className="mr-1" aria-hidden />
                    List your venue
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Filter row — the whole page in one control. Sticky, because on a
            phone the answer to "does it have a pool table" is the reason the
            user is here and they will want to change their mind mid-scroll. */}
        <div className={cn("sticky z-30 bg-white/95 backdrop-blur border-t border-slate-100", STICKY_TOP)}>
          <div className="container mx-auto px-4 flex items-center gap-2">
            <div
              role="group"
              aria-label="Filter by what the venue has"
              className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto scrollbar-none py-1"
            >
              {PLACE_FILTERS.map((f) => {
                const active = selected.includes(f.slug);
                const Icon = f.icon;
                return (
                  <button
                    key={f.slug}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggle(f.slug)}
                    className={cn(
                      TYPE.caption, RADII.pill, FOCUS_RING, TAP_TARGET,
                      "inline-flex shrink-0 items-center gap-1.5 px-3.5 h-9 border transition-colors cursor-pointer whitespace-nowrap",
                      active
                        ? "bg-[#1e3a5f] text-white border-[#1e3a5f]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
                    )}
                  >
                    <Icon size={13} aria-hidden />
                    {f.label}
                  </button>
                );
              })}
            </div>
            <TownPicker value={town} onChange={(t) => apply(t, selected)} />
          </div>
        </div>
      </div>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <div className="container mx-auto px-4 py-4 sm:py-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className={cn(TYPE.footnote, "text-slate-500")}>
            {isLoading
              ? "Looking for places…"
              : `${venues.length} ${venues.length === 1 ? noun.replace(/s$/, "") : noun} in ${townLabel}${phrase ? ` ${phrase}` : ""}`}
          </p>
          {hasFilter && (
            <FilterChip icon={<X size={12} aria-hidden />} onClick={() => apply(ALL_TOWNS, [])}>
              Clear
            </FilterChip>
          )}
        </div>

        <QueryState
          isLoading={isLoading}
          isError={venuesQuery.isError}
          isEmpty={venues.length === 0}
          onRetry={venuesQuery.refetch}
          skeleton={skeleton}
          errorTitle="Couldn't load places"
          errorBody="We couldn't reach Ayrshire Hub just now. Check your connection and try again."
          empty={
            <NoPlaces
              town={town}
              townLabel={townLabel}
              noun={noun}
              phrase={phrase}
              selected={selected}
              onApply={apply}
            />
          }
        >
          <div className={BROWSE_GRID}>
            {venues.map((venue) => (
              <VenueCard
                key={venue.id}
                venue={venue}
                facilities={resolveFacilities(facilityMap.get(venue.id) ?? [], definitions)}
                whatsOnLine={whatsOnLine(venue)}
              />
            ))}
          </div>
        </QueryState>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <Link
            href="/events"
            className={cn(
              RADII.md, FOCUS_RING,
              "flex items-center gap-3 border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-slate-300",
            )}
          >
            <span className="text-xl" aria-hidden>🎪</span>
            <span className="flex-1 min-w-0">
              <span className={cn(TYPE.bodyStrong, "block text-slate-900")}>Want a date, not a place?</span>
              <span className={cn(TYPE.footnote, "block text-slate-500")}>
                See what's on tonight, tomorrow or this weekend
              </span>
            </span>
            <ChevronRight size={16} className="text-slate-400 shrink-0" aria-hidden />
          </Link>

          <Link
            href="/list-your-venue"
            className={cn(
              RADII.md, FOCUS_RING,
              "flex items-center gap-3 border border-amber-200 bg-amber-50 px-4 py-3 transition-colors hover:bg-amber-100",
            )}
          >
            <span className="text-xl" aria-hidden>🍺</span>
            <span className="flex-1 min-w-0">
              <span className={cn(TYPE.bodyStrong, "block text-amber-900")}>Is your pub missing?</span>
              <span className={cn(TYPE.footnote, "block text-amber-800/80")}>
                List it free and tick what you've got — pool, darts, garden, Sky
              </span>
            </span>
            <ChevronRight size={16} className="text-amber-700 shrink-0" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── Empty state ──────────────────────────────────────────────────────────
   Same principle as What's On: widen ONE axis at a time and say which, rather
   than dumping the user back at square one. With AND semantics the most
   likely cause of an empty result is one chip too many, so dropping the last
   facility is offered first.                                                 */

function NoPlaces({
  town, townLabel, noun, phrase, selected, onApply,
}: {
  town: string;
  townLabel: string;
  noun: string;
  phrase: string;
  selected: string[];
  onApply: (town: string, filters: string[]) => void;
}) {
  const last = selected.length > 0 ? PLACE_FILTER_BY_SLUG[selected.at(-1)!] : undefined;

  return (
    <div className={cn(RADII.md, ELEVATION.sm, "bg-white border border-slate-200/80 p-5 sm:p-6")}>
      <p className={cn(TYPE.title3, "text-slate-900")}>
        No {noun} in {townLabel}{phrase ? ` ${phrase}` : ""}.
      </p>
      <p className={cn(TYPE.callout, "text-slate-500 mt-1")}>
        {selected.length > 1
          ? "You've asked for a few things at once — every one has to match."
          : town !== ALL_TOWNS
            ? "Only a handful of venues have listed themselves so far."
            : "Nothing listed yet — venues are still being added."}
      </p>

      <div className="flex flex-wrap gap-2 mt-4">
        {selected.length > 1 && last && (
          <FilterChip onClick={() => onApply(town, selected.slice(0, -1))}>
            Drop “{last.label}”
          </FilterChip>
        )}
        {town !== ALL_TOWNS && selected.length > 0 && (
          <FilterChip onClick={() => onApply(ALL_TOWNS, selected)}>
            Look across all of Ayrshire
          </FilterChip>
        )}
        {town !== ALL_TOWNS && selected.length === 0 && (
          <FilterChip onClick={() => onApply(ALL_TOWNS, [])}>All of Ayrshire</FilterChip>
        )}
        {selected.length > 0 && (
          <FilterChip onClick={() => onApply(town, [])}>Show every place in {townLabel}</FilterChip>
        )}
      </div>

      <Link
        href="/list-your-venue"
        className={cn(
          RADII.sm, FOCUS_RING, TYPE.callout,
          "inline-flex items-center gap-1.5 mt-5 px-4 py-2.5 bg-[#1e3a5f] text-white font-semibold hover:bg-[#1e3a5f]/90 transition-colors",
        )}
      >
        List your venue — it's free
        <ChevronRight size={14} aria-hidden />
      </Link>
    </div>
  );
}
